import {
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import {
  ActivatedRoute,
  Event,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterEvent,
  RouterOutlet,
} from '@angular/router';
import { DialogService } from '@ui-components/dialog/services/dialog.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { StorageContextService } from './context/storage-context/storage-context.service';
import { version } from '../environments/version';
import { debounceTime, distinctUntilChanged, filter, skip } from 'rxjs/operators';
import { GoogleAnalyticsService } from './core/services/google-analytics/google-analytics.service';
import { PixelsService } from './core/services/pixels/pixels.service';
import { SEOService } from './core/services/seo/seo.service';
import { LocaleService } from './core/services/locale/locale.service';
import { UtmTagsService } from './core/services/utm-tags-service/utm-tags-service';
import { NotificationService } from './shared/components/ui/notification/services/notification.service';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { startWith } from 'rxjs';
import { LoadingLogoComponent } from './shared/components/ui/loading-logo/loading-logo.component';
import { CookieNoticeComponent } from './core/components/cookie-notice/cookie-notice.component';
import { LoadingComponent } from './shared/components/ui/loading/loading.component';
import { SpriteSvgComponent } from './shared/components/sprite-svg/sprite-svg.component';
import { GlobalLoadingIndicatorService } from './services/global-loading-indicator/global-loading-indicator.service';
import { AnalyticsService } from './core/services/analytics/analytics.service';
import { AlertContainerComponent } from '@ui-components/alert';
import { WebviewService } from './core/services/webview/webview.service';
import { AuthService } from './auth/services/auth.service';
import { OfflineBannerComponent } from './core/network/offline-banner.component';
import { TestCountryBadgeComponent } from './core/components/test-country-badge/test-country-badge.component';

export type Lang = 'en' | 'de' | 'es' | 'pt';

export function isLang(lang: Lang): lang is Lang {
  return lang === 'en' || lang === 'de' || lang === 'es' || lang === 'pt';
}

@UntilDestroy()
@Component({
  selector: 'my-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    LoadingLogoComponent,
    CookieNoticeComponent,
    LoadingComponent,
    SpriteSvgComponent,
    RouterOutlet,
    AlertContainerComponent,
    OfflineBannerComponent,
    TestCountryBadgeComponent,
  ],
})
export class AppComponent implements OnInit {
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly storageContext = inject(StorageContextService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationService = inject(NotificationService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly pixels = inject(PixelsService);
  private readonly seoService = inject(SEOService);
  readonly localeService = inject(LocaleService);
  private readonly utmTagsService = inject(UtmTagsService);
  readonly globalLoader = inject(GlobalLoadingIndicatorService);
  readonly analytics = inject(AnalyticsService);
  private readonly webviewService = inject(WebviewService);
  private readonly authService = inject(AuthService);

  cookieNoticeDisabled: boolean;
  readonly loading = signal(false);
  readonly isLoadingLogo$ = computed(() => this.loading() || this.globalLoader.isLoading());
  readonly showLoadingLogo = toSignal(
    toObservable(this.isLoadingLogo$).pipe(
      distinctUntilChanged(),
      skip(1),
      debounceTime(500),
      startWith(true)
    )
  );
  version = version;

  @ViewChild('dialogContainer', { read: ViewContainerRef, static: true })
  dialogContainerRef: ViewContainerRef;

  @ViewChild('notificationContainer', { read: ViewContainerRef, static: true })
  notificationContainerRef: ViewContainerRef;

  constructor() {
    this.analytics.init().then();
  }

  private setFromParam(): void {
    const from = this.route.snapshot.queryParams['from'];
    if (from) {
      this.storageContext.setItem('from', from);
    }
  }

  private navigationStart(e: RouterEvent | Event): boolean {
    return e instanceof NavigationStart;
  }

  private navigationEnd(e: RouterEvent | Event): boolean {
    return e instanceof NavigationEnd;
  }

  private navigationOther(e: RouterEvent | Event): boolean {
    return (
      e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError
    );
  }

  ngOnInit(): void {
    this.localeService.initLocale();
    this.authService.scheduleProactiveRefresh();

    console.log(version);

    this.cookieNoticeDisabled = !!this.storageContext.getItem('cookie-notice');

    window.addEventListener('pageshow', (event: PageTransitionEvent) => {
      if (event.persisted) {
        this.loading.set(false);
      }
    });

    this.router.events
      .pipe(
        filter((e: RouterEvent | Event) => this.navigationStart(e) || this.navigationOther(e)),
        untilDestroyed(this)
      )
      .subscribe((event: RouterEvent | Event) => {
        if (this.navigationStart(event)) {
          this.loading.set(true);
          this.webviewService.check();
        }

        if (this.navigationEnd(event)) {
          this.googleAnalyticsService.pageView(event);
          this.pixels.pageView();

          if (event instanceof NavigationEnd) {
            this.seoService.setTitleAndDescription(this.seoLocation(event?.url));
            // this.analytics.pageViewed().then();

            this.cookieNoticeDisabled =
              event.url.includes('profile/subscriptions') ||
              event.url.includes('/app/profile/edit') ||
              event.url.includes('payment') ||
              this.cookieNoticeDisabled;

            if (event.url.includes('/quiz69')) {
              const segments = event.url.split('/');
              let lang: Lang | 'en-US' = segments?.[segments.length - 1]?.slice(0, 2) as Lang;

              lang = isLang(lang) ? lang : 'en-US';

              if (lang === 'en') {
                lang = 'en-US';
              }

              this.localeService.setLocale(lang);
              this.storageContext.setItem('lang', lang);
            }

            if (event.url.includes('prefix=quiz69')) {
              this.localeService.setLocale(this.storageContext.getItem('lang'));
            }

            this.utmTagsService.initUtmTagsConversion();
          }
        }

        if (this.navigationOther(event)) {
          this.setFromParam();
          this.loading.set(false);
        }
      });

    this.dialogService.registerViewContainer(this.dialogContainerRef);
    this.notificationService.registerViewContainer(this.notificationContainerRef);
  }

  disableCookieNotice(): void {
    this.cookieNoticeDisabled = true;
  }

  private seoLocation(url: string): string {
    if (url.includes('home') || url === '/') {
      return 'home';
    } else if (url.includes('packages')) {
      return 'packages';
    } else if (url.includes('challenges')) {
      return 'challenges';
    } else if (url.includes('gift')) {
      return 'giftCertificate';
    } else return 'default';
  }
}
